"use client";

import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarChart3Icon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  TableIcon,
} from "lucide-react";
import { type CSSProperties, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  type Cell,
  chartSeries,
  csvFileName,
  formatCell,
  niceTicks,
  readVisual,
  seriesColor,
  sortRows,
  toCsv,
  toTsv,
  type Visual,
} from "@/lib/visuals";
import { TooltipIconButton } from "./tooltip-icon-button";

/**
 * A table or chart in an answer (ADMIN-201): the model's `show_visual` call, rendered where it
 * sits between the answer's paragraphs. Charts are drawn by hand in SVG and HTML with Kourai's
 * chart tokens, so they follow light and dark mode and need no chart library. Every chart can
 * switch to its table, which is also how a screen reader gets the numbers.
 */
export const VisualAnswerToolUI: ToolCallMessagePartComponent = ({ args, result, status }) => {
  const visual = useMemo(() => readVisual(args), [args]);
  const failed = !!result && typeof result === "object" && "error" in result;
  const drawing = status.type === "running" || status.type === "requires-action";

  if (failed) return null; // The model was told why and draws it again.
  if (drawing || !visual) {
    if (!drawing) return null;
    const title = (args as { title?: unknown })?.title;
    return (
      <div
        role="status"
        data-slot="visual-answer-pending"
        className="border-border text-muted-foreground my-4 flex h-40 items-center justify-center border text-sm"
      >
        <span className="animate-pulse">
          {typeof title === "string" && title ? `Drawing ${title}` : "Drawing a chart"}
        </span>
      </div>
    );
  }
  return <VisualAnswer visual={visual} />;
};

/** A visual, drawn: also what a pinned answer on the board shows (ADMIN-266). */
export function VisualAnswer({ visual }: { visual: Visual }) {
  const [asTable, setAsTable] = useState(visual.kind === "table");
  const isChart = visual.kind !== "table";
  const label = visual.title ?? (isChart ? "Chart" : "Table");

  return (
    <figure
      data-slot="visual-answer"
      data-kind={visual.kind}
      className="border-border bg-card my-4 border"
    >
      <div className="border-border flex items-center gap-2 border-b py-1.5 ps-3 pe-1.5">
        <figcaption className="min-w-0 flex-1 truncate text-sm font-medium">{label}</figcaption>
        <div className="text-muted-foreground flex shrink-0 items-center gap-0.5">
          {isChart && (
            <TooltipIconButton
              tooltip={asTable ? "Show chart" : "Show table"}
              aria-pressed={asTable}
              onClick={() => setAsTable((v) => !v)}
            >
              {asTable ? <BarChart3Icon /> : <TableIcon />}
            </TooltipIconButton>
          )}
          <CopyDataButton visual={visual} />
          <TooltipIconButton tooltip="Download CSV" onClick={() => downloadCsv(visual)}>
            <DownloadIcon />
          </TooltipIconButton>
        </div>
      </div>
      <div className="p-3">
        {asTable || !isChart ? (
          <DataTable visual={visual} />
        ) : visual.kind === "bar" ? (
          <BarChart visual={visual} />
        ) : visual.kind === "line" ? (
          <LineChart visual={visual} />
        ) : (
          <PieChart visual={visual} />
        )}
      </div>
      {visual.note && (
        <p className="text-muted-foreground border-border border-t px-3 py-1.5 text-xs">
          {visual.note}
        </p>
      )}
    </figure>
  );
}

function CopyDataButton({ visual }: { visual: Visual }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <TooltipIconButton
      tooltip={copied ? "Copied" : "Copy data"}
      onClick={() => {
        void navigator.clipboard.writeText(toTsv(visual)).then(() => setCopied(true));
      }}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </TooltipIconButton>
  );
}

function downloadCsv(visual: Visual) {
  const url = URL.createObjectURL(new Blob([toCsv(visual)], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = csvFileName(visual);
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type Sort = { column: number; direction: "asc" | "desc" } | null;

function DataTable({ visual }: { visual: Visual }) {
  const [sort, setSort] = useState<Sort>(null);
  const rows = sort ? sortRows(visual.rows, sort.column, sort.direction) : visual.rows;
  const numeric = visual.columns.map((c) => c.format !== "text");

  const toggle = (column: number) =>
    setSort((s) =>
      s?.column !== column
        ? { column, direction: numeric[column] ? "desc" : "asc" }
        : { column, direction: s.direction === "asc" ? "desc" : "asc" },
    );

  return (
    <div className="-m-3 max-h-96 overflow-auto">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-card sticky top-0">
          <tr className="border-border border-b">
            {visual.columns.map((column, i) => {
              const active = sort?.column === i;
              return (
                <th
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                  key={i}
                  scope="col"
                  aria-sort={
                    active ? (sort.direction === "asc" ? "ascending" : "descending") : undefined
                  }
                  className={cn("p-0 font-medium", numeric[i] ? "text-right" : "text-left")}
                >
                  <button
                    type="button"
                    onClick={() => toggle(i)}
                    className={cn(
                      "text-muted-foreground hover:text-foreground inline-flex w-full items-center gap-1 px-3 py-2 outline-none focus-visible:underline",
                      numeric[i] && "flex-row-reverse",
                      active && "text-foreground",
                    )}
                  >
                    <span>{column.label}</span>
                    {active &&
                      (sort.direction === "asc" ? (
                        <ArrowUpIcon className="size-3" />
                      ) : (
                        <ArrowDownIcon className="size-3" />
                      ))}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no id, and sorting re-renders them all
            <tr key={r} className="border-border border-b last:border-0">
              {row.map((value, c) => {
                const column = visual.columns[c];
                if (!column) return null;
                const Cell = c === 0 ? "th" : "td";
                return (
                  <Cell
                    // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                    key={c}
                    scope={c === 0 ? "row" : undefined}
                    className={cn(
                      "px-3 py-2 align-top",
                      c === 0 && "font-normal",
                      numeric[c] ? "text-right tabular-nums" : "text-left",
                    )}
                  >
                    {formatCell(value, column)}
                  </Cell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  if (items.length < 2) return null;
  return (
    <ul className="text-muted-foreground mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {items.map((item) => (
        <li key={item.label} className="inline-flex items-center gap-1.5">
          <span aria-hidden className="size-2.5" style={{ background: item.color }} />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/** Horizontal bars: the labels stay readable in a narrow side panel. */
function BarChart({ visual }: { visual: Visual }) {
  const series = chartSeries(visual);
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const ticks = niceTicks(all);
  const min = ticks[0] ?? 0;
  const max = ticks.at(-1) ?? 1;
  const span = max - min || 1;
  const zero = ((0 - min) / span) * 100;
  const labelColumn = visual.columns[0];

  return (
    <div role="img" aria-label={describeChart(visual)}>
      <Legend items={series.map((s, i) => ({ label: s.column.label, color: seriesColor(i) }))} />
      <div className="grid grid-cols-[minmax(4rem,max-content)_1fr] items-center gap-x-3 gap-y-2.5">
        {visual.rows.map((row, r) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
          <Fragment key={r}>
            <span
              className="text-muted-foreground max-w-40 truncate text-xs"
              title={String(row[0] ?? "")}
            >
              {labelColumn ? formatCell(row[0] ?? null, labelColumn) : ""}
            </span>
            <div className="flex flex-col gap-0.5 pe-14">
              {series.map((s, i) => {
                const value = s.values[r] ?? null;
                const width = value === null ? 0 : (Math.abs(value) / span) * 100;
                const left = value !== null && value < 0 ? zero - width : zero;
                return (
                  <div key={s.index} className="relative h-4">
                    <div
                      className="absolute inset-y-0"
                      style={{ left: `${left}%`, width: `${width}%`, background: seriesColor(i) }}
                    />
                    <span
                      className="absolute top-1/2 -translate-y-1/2 text-[11px] whitespace-nowrap tabular-nums"
                      style={{ left: `calc(${left + width}% + 4px)` }}
                    >
                      {formatCell(value, s.column, true)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

const LINE_HEIGHT = 200;
const PAD = { top: 8, right: 12, bottom: 22, left: 44 };

function LineChart({ visual }: { visual: Visual }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const series = chartSeries(visual);
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const ticks = niceTicks(all);
  const min = ticks[0] ?? 0;
  const max = ticks.at(-1) ?? 1;
  const n = visual.rows.length;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = LINE_HEIGHT - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - ((v - min) / (max - min || 1)) * plotH;
  const labelColumn = visual.columns[0];
  const firstSeries = series[0]?.column;
  // At most ~6 x labels, evenly spaced, always including the last.
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 72))));

  const paths = series.map((s) => {
    let d = "";
    s.values.forEach((v, i) => {
      if (v === null) return;
      d += `${d && s.values[i - 1] !== null ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    });
    return d;
  });

  return (
    <div role="img" aria-label={describeChart(visual)}>
      <Legend items={series.map((s, i) => ({ label: s.column.label, color: seriesColor(i) }))} />
      <div ref={ref} className="relative" style={{ height: LINE_HEIGHT }}>
        {width > 0 && (
          <svg
            width={width}
            height={LINE_HEIGHT}
            className="overflow-visible"
            onPointerLeave={() => setHover(null)}
            onPointerMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const px = e.clientX - rect.left - PAD.left;
              const i = n === 1 ? 0 : Math.round((px / (plotW || 1)) * (n - 1));
              setHover(Math.min(n - 1, Math.max(0, i)));
            }}
          >
            <title>{visual.title ?? "Line chart"}</title>
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={PAD.left}
                  x2={PAD.left + plotW}
                  y1={y(t)}
                  y2={y(t)}
                  stroke="var(--border)"
                  strokeDasharray={t === 0 ? undefined : "2 3"}
                />
                <text
                  x={PAD.left - 6}
                  y={y(t)}
                  dy="0.32em"
                  textAnchor="end"
                  className="fill-muted-foreground text-[10px] tabular-nums"
                >
                  {firstSeries ? formatCell(t, firstSeries, true) : t}
                </text>
              </g>
            ))}
            {visual.rows.map((row, i) =>
              i % every === 0 || i === n - 1 ? (
                <text
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
                  key={i}
                  x={x(i)}
                  y={LINE_HEIGHT - 6}
                  textAnchor={i === 0 && n > 1 ? "start" : i === n - 1 && n > 1 ? "end" : "middle"}
                  className="fill-muted-foreground text-[10px]"
                >
                  {labelColumn ? formatCell(row[0] ?? null, labelColumn) : ""}
                </text>
              ) : null,
            )}
            {hover !== null && (
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={PAD.top}
                y2={PAD.top + plotH}
                stroke="var(--muted-foreground)"
                strokeWidth={1}
              />
            )}
            {paths.map((d, i) => (
              <path
                key={series[i]?.index}
                data-slot="visual-series"
                d={d}
                fill="none"
                stroke={seriesColor(i)}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
            {series.map((s, i) =>
              s.values.map((v, r) =>
                v === null || (n > 24 && r !== hover) ? null : (
                  <circle
                    // biome-ignore lint/suspicious/noArrayIndexKey: points are positional
                    key={`${s.index}-${r}`}
                    cx={x(r)}
                    cy={y(v)}
                    r={r === hover ? 4 : 2.5}
                    fill="var(--background)"
                    stroke={seriesColor(i)}
                    strokeWidth={2}
                  />
                ),
              ),
            )}
          </svg>
        )}
        {hover !== null && width > 0 && (
          <div
            className="bg-popover text-popover-foreground border-border pointer-events-none absolute top-0 z-10 border px-2 py-1 text-xs shadow-sm"
            style={
              {
                left: x(hover),
                transform: `translateX(${x(hover) > width / 2 ? "calc(-100% - 8px)" : "8px"})`,
              } as CSSProperties
            }
          >
            <div className="font-medium">
              {labelColumn ? formatCell(visual.rows[hover]?.[0] ?? null, labelColumn) : ""}
            </div>
            {series.map((s, i) => (
              <div key={s.index} className="flex items-center gap-1.5 tabular-nums">
                <span aria-hidden className="size-2" style={{ background: seriesColor(i) }} />
                {series.length > 1 && (
                  <span className="text-muted-foreground">{s.column.label}</span>
                )}
                <span>{formatCell(s.values[hover] ?? null, s.column)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PIE_SIZE = 140;

function PieChart({ visual }: { visual: Visual }) {
  const s = chartSeries(visual)[0];
  const labelColumn = visual.columns[0];
  if (!s || !labelColumn) return null;
  const values = s.values.map((v) => (v !== null && v > 0 ? v : 0));
  const total = values.reduce((a, b) => a + b, 0) || 1;
  const r = PIE_SIZE / 2;
  const inner = r * 0.58;
  let angle = -Math.PI / 2;

  const slices = values.map((v, i) => {
    const start = angle;
    const sweep = (v / total) * Math.PI * 2;
    angle += sweep;
    return { i, start, sweep, value: v };
  });

  const point = (a: number, radius: number) =>
    `${(r + radius * Math.cos(a)).toFixed(2)},${(r + radius * Math.sin(a)).toFixed(2)}`;

  return (
    <div
      role="img"
      aria-label={describeChart(visual)}
      className="flex flex-wrap items-center gap-6"
    >
      <svg
        width={PIE_SIZE}
        height={PIE_SIZE}
        viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`}
        className="shrink-0"
      >
        <title>{visual.title ?? "Pie chart"}</title>
        {slices.map(({ i, start, sweep }) => {
          if (sweep <= 0) return null;
          if (sweep >= Math.PI * 2 - 1e-6) {
            return (
              <circle
                key={i}
                data-slot="visual-series"
                cx={r}
                cy={r}
                r={(r + inner) / 2}
                fill="none"
                stroke={seriesColor(i)}
                strokeWidth={r - inner}
              />
            );
          }
          const end = start + sweep;
          const large = sweep > Math.PI ? 1 : 0;
          const d = `M${point(start, r)}A${r},${r} 0 ${large} 1 ${point(end, r)}L${point(end, inner)}A${inner},${inner} 0 ${large} 0 ${point(start, inner)}Z`;
          return (
            <path
              key={i}
              data-slot="visual-series"
              d={d}
              fill={seriesColor(i)}
              stroke="var(--card)"
              strokeWidth={1.5}
            />
          );
        })}
      </svg>
      <ul className="flex min-w-0 flex-col gap-1.5 text-sm">
        {visual.rows.map((row, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
          <li key={i} className="flex items-center gap-2">
            <span
              aria-hidden
              className="size-2.5 shrink-0"
              style={{ background: seriesColor(i) }}
            />
            <span className="truncate">{formatCell(row[0] ?? null, labelColumn)}</span>
            <span className="text-muted-foreground ms-auto ps-3 tabular-nums">
              {formatCell(s.values[i] ?? null, s.column)} ·{" "}
              {Math.round(((values[i] ?? 0) / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** What a chart shows, in words, for screen readers. The table toggle has every value. */
function describeChart(visual: Visual): string {
  const kind = { bar: "Bar chart", line: "Line chart", pie: "Pie chart", table: "Table" }[
    visual.kind
  ];
  const series = visual.columns
    .slice(1)
    .map((c) => c.label)
    .join(", ");
  const first = visual.rows[0]?.[0];
  const last = visual.rows.at(-1)?.[0];
  const range: Cell[] = visual.rows.length > 1 ? [first ?? null, last ?? null] : [first ?? null];
  return `${kind}${visual.title ? `: ${visual.title}` : ""}. ${series} for ${visual.rows.length} ${
    visual.rows.length === 1 ? "row" : "rows"
  }, ${range.filter((v) => v !== null).join(" to ")}.`;
}
